import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Select, Progress, Spin, Alert } from 'antd';
import apiService from '../services/apiService';
import useSettingsStore from '../store/settingsStore';

const { Title, Text } = Typography;
const { Option } = Select;

const getCurrentQuarter = () => {
  const month = new Date().getMonth() + 1;
  if (month >= 4 && month <= 6) return 'Q1';
  if (month >= 7 && month <= 9) return 'Q2';
  if (month >= 10 && month <= 12) return 'Q3';
  return 'Q4';
};

const aggregateTargets = (targets, year) => {
  const result = {
    year,
    annual: null,
    quarters: []
  };

  if (!Array.isArray(targets) || !targets.length) {
    return result;
  }

  const annualTargets = targets.filter(t => t.type === 'annual');
  const quarterlyTargets = targets.filter(t => t.type === 'quarterly');

  if (annualTargets.length) {
    const totalAnnualAmount = annualTargets.reduce((sum, t) => sum + (t.targetAmount || 0), 0);
    const totalAnnualAchieved = annualTargets.reduce((sum, t) => sum + (t.achievedAmount || 0), 0);
    result.annual = {
      year,
      type: 'annual',
      targetAmount: totalAnnualAmount,
      achievedAmount: totalAnnualAchieved,
      currency: annualTargets[0].currency || 'INR'
    };
  }

  const quarterMap = {};
  quarterlyTargets.forEach(t => {
    const key = t.period;
    if (!key) return;
    if (!quarterMap[key]) {
      quarterMap[key] = {
        period: key,
        targetAmount: 0,
        achievedAmount: 0,
        currency: t.currency || 'INR'
      };
    }
    quarterMap[key].targetAmount += t.targetAmount || 0;
    quarterMap[key].achievedAmount += t.achievedAmount || 0;
  });

  result.quarters = Object.values(quarterMap);
  return result;
};

const TenantAdminTargetsSettings = () => {
  const { formatCurrency } = useSettingsStore();
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState(getCurrentQuarter());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [annualTarget, setAnnualTarget] = useState(null);
  const [quarterTargets, setQuarterTargets] = useState([]);

  useEffect(() => {
    const fetchTargets = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiService.get(`/targets?year=${selectedYear}`);
        if (response.data?.success) {
          const aggregated = aggregateTargets(response.data.data, selectedYear);
          setAnnualTarget(aggregated.annual);
          setQuarterTargets(aggregated.quarters);
        } else {
          setAnnualTarget(null);
          setQuarterTargets([]);
          setError(response.data?.message || 'Failed to load targets');
        }
      } catch (err) {
        setAnnualTarget(null);
        setQuarterTargets([]);
        setError(err.response?.data?.message || 'Failed to load targets');
      } finally {
        setLoading(false);
      }
    };

    fetchTargets();
  }, [selectedYear]);

  const selectedQuarterData = useMemo(
    () => quarterTargets.find(q => q.period === selectedQuarter) || null,
    [quarterTargets, selectedQuarter]
  );

  // For tenant_admin cumulative view, prioritize quarterly totals (same as TargetSidebar)
  const quarterlyTotal = useMemo(() => {
    if (!quarterTargets || quarterTargets.length === 0) return null;
    return {
      targetAmount: quarterTargets.reduce((sum, q) => sum + (q.targetAmount || 0), 0),
      achievedAmount: quarterTargets.reduce((sum, q) => sum + (q.achievedAmount || 0), 0),
      currency: quarterTargets[0]?.currency || annualTarget?.currency || 'INR'
    };
  }, [quarterTargets, annualTarget]);

  const calculatedAnnualTarget = useMemo(() => {
    // Match TargetSidebar logic for tenant_admin: use quarters first, then annual
    return quarterlyTotal || annualTarget || null;
  }, [quarterlyTotal, annualTarget]);

  const annualProgress = useMemo(() => {
    if (!calculatedAnnualTarget || !calculatedAnnualTarget.targetAmount) return 0;
    return (
      (calculatedAnnualTarget.achievedAmount / calculatedAnnualTarget.targetAmount) * 100
    );
  }, [calculatedAnnualTarget]);

  const quarterProgress = useMemo(() => {
    if (!selectedQuarterData || !selectedQuarterData.targetAmount) return 0;
    return (selectedQuarterData.achievedAmount / selectedQuarterData.targetAmount) * 100;
  }, [selectedQuarterData]);

  const renderAnnualCard = () => (
    <Card
      title={
        <span>
          Annual Target
          {!annualTarget && quarterTargets.length > 0 ? ' (from Quarters)' : ''}
        </span>
      }
      className="h-full"
    >
      {loading ? (
        <div className="text-center py-8">
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert type="error" message={error} />
      ) : !calculatedAnnualTarget ? (
        <div className="text-center py-8">
          <Text type="secondary">No annual target set for {selectedYear}</Text>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <Text className="text-xs text-gray-500 block mb-1">Target</Text>
            <Title level={4} className="mb-0">
              {formatCurrency(calculatedAnnualTarget.targetAmount || 0)}
            </Title>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Text className="text-xs text-gray-500 block mb-1">Achieved</Text>
              <Text strong>
                {formatCurrency(calculatedAnnualTarget.achievedAmount || 0)}
              </Text>
            </div>
            <div>
              <Text className="text-xs text-gray-500 block mb-1">Remaining</Text>
              <Text strong type="warning">
                {formatCurrency(
                  (calculatedAnnualTarget.targetAmount || 0) -
                  (calculatedAnnualTarget.achievedAmount || 0)
                )}
              </Text>
            </div>
          </div>
          <div>
            <Text className="text-xs text-gray-500 block mb-1">Progress</Text>
            <Progress
              percent={Number(annualProgress.toFixed(2))}
              strokeColor="#52c41a"
            />
          </div>
        </div>
      )}
    </Card>
  );

  const renderQuarterCard = () => (
    <Card title={`${selectedQuarter} Target`} className="h-full">
      {loading ? (
        <div className="text-center py-8">
          <Spin size="large" />
        </div>
      ) : error ? (
        <Alert type="error" message={error} />
      ) : !selectedQuarterData ? (
        <div className="text-center py-8">
          <Text type="secondary">No target set for {selectedQuarter} {selectedYear}</Text>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <Text className="text-xs text-gray-500 block mb-1">Target</Text>
            <Title level={4} className="mb-0">
              {formatCurrency(selectedQuarterData.targetAmount || 0)}
            </Title>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Text className="text-xs text-gray-500 block mb-1">Achieved</Text>
              <Text strong>
                {formatCurrency(selectedQuarterData.achievedAmount || 0)}
              </Text>
            </div>
            <div>
              <Text className="text-xs text-gray-500 block mb-1">Remaining</Text>
              <Text strong type="warning">
                {formatCurrency((selectedQuarterData.targetAmount || 0) - (selectedQuarterData.achievedAmount || 0))}
              </Text>
            </div>
          </div>
          <div>
            <Text className="text-xs text-gray-500 block mb-1">Progress</Text>
            <Progress
              percent={Number(quarterProgress.toFixed(2))}
              strokeColor="#52c41a"
            />
          </div>
        </div>
      )}
    </Card>
  );

  return (
    <Card
      className="shadow-soft"
      title={
        <div className="flex justify-between items-center">
          <span>Targets</span>
          <div className="flex space-x-2">
            <Select
              size="middle"
              value={selectedYear}
              onChange={setSelectedYear}
              style={{ width: 100 }}
            >
              {[currentYear - 1, currentYear, currentYear + 1].map(year => (
                <Option key={year} value={year}>{year}</Option>
              ))}
            </Select>
            <Select
              size="middle"
              value={selectedQuarter}
              onChange={setSelectedQuarter}
              style={{ width: 80 }}
            >
              <Option value="Q1">Q1</Option>
              <Option value="Q2">Q2</Option>
              <Option value="Q3">Q3</Option>
              <Option value="Q4">Q4</Option>
            </Select>
          </div>
        </div>
      }
    >
      <Row gutter={16}>
        <Col xs={24} md={12}>
          {renderAnnualCard()}
        </Col>
        <Col xs={24} md={12}>
          {renderQuarterCard()}
        </Col>
      </Row>
    </Card>
  );
};

export default TenantAdminTargetsSettings;
